import { useQuery } from '@tanstack/react-query';
import { create, windowScheduler, keyResolver } from '@yornaath/batshit';

import { SystemCatalog } from '@graylog/server-api';

import type { Assignee } from 'security-app/components/Investigations/hooks/useUsersAndTeams';

type EntityTitleResponse = {
  id: string;
  type: string;
  title: string;
};

const ownerTitles = create({
  fetcher: (ids: Array<string>) =>
    SystemCatalog.getTitles({
      entities: ids.flatMap((id) => [
        { id, type: 'users' },
        { id, type: 'teams' },
      ]),
    }).then(({ entities }) => entities),
  resolver: keyResolver('id'),
  scheduler: windowScheduler(10),
});

const toAssignee = (entity: EntityTitleResponse): Assignee | undefined => {
  if (!entity?.title) {
    return undefined;
  }

  return {
    id: entity.id,
    label: entity.title,
    type: entity.type === 'teams' ? 'team' : 'user',
  };
};

const isAdminOwnerId = (ownerId: string) => ownerId?.endsWith('admin');

export const fetchOwnerTitle = (ownerId: string): Promise<Assignee> =>
  isAdminOwnerId(ownerId)
    ? Promise.resolve({ id: ownerId, label: 'Administrator', type: 'user' })
    : ownerTitles.fetch(ownerId).then((owner) => toAssignee(owner));

export const fetchOwnerTitles = async (ownerIds: Array<string>): Promise<Array<Assignee>> => {
  if (!ownerIds?.length) {
    return [];
  }

  const uniqueOwnerIds = Array.from(new Set(ownerIds.filter(Boolean)));
  const entities = await Promise.all(uniqueOwnerIds.map((ownerId) => fetchOwnerTitle(ownerId)));

  return entities.filter(Boolean);
};

const useOwnerAssignee = (
  ownerId: string | null,
): {
  data: Assignee | undefined;
  isInitialLoading: boolean;
} => {
  const { data, isInitialLoading } = useQuery({
    queryKey: ['security_events', 'owner', ownerId],
    queryFn: () => fetchOwnerTitle(ownerId),
    enabled: !!ownerId,
  });

  return {
    data,
    isInitialLoading,
  };
};

export default useOwnerAssignee;
